import React, { useState, useEffect } from 'react';
import { CheckCircle2, Shield, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';

const ACKNOWLEDGEMENT_TEXT = `
Welcome to the Leynix Pilot Program

Thank you for participating in the first pilot implementation of Leynix, an enterprise operations platform developed and owned by Anessa Leydig.

A Touch of Angels LLC has been selected as the pilot organization to help evaluate the platform in a real operational environment. During this pilot, features, workflows, and functionality may change as improvements are made.

By selecting Accept & Continue, you acknowledge that:

• You understand you are participating in a pilot program.

• Leynix is proprietary software and all intellectual property rights remain the exclusive property of Anessa Leydig.

• Information, screenshots, documentation, and software features should not be copied, distributed, or shared outside of A Touch of Angels LLC without prior written permission.

• Your feedback and suggestions are appreciated and may be incorporated into future versions of the platform.

• You will use Leynix only for authorized business purposes during the pilot.

• The Agency (A Touch of Angels LLC) retains ownership of all business records and operational data entered into Leynix. Participation in the pilot does not transfer ownership of Agency data.
`;

export default function PilotAcknowledgement({ onAccepted, userName }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = async () => {
    if (!checked) {
      setError('Please check the box to confirm you have read and understand the acknowledgement.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.post('/pilot/acknowledgement/accept', { confirmed: true });
      onAccepted();
    } catch (err) {
      console.error('Failed to accept acknowledgement:', err);
      setError('Failed to record your acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1B33] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#176DFF]/10 mb-4">
            <Shield className="w-8 h-8 text-[#176DFF]" />
          </div>
          <h1 className="text-2xl font-bold text-white">Pilot Participation Acknowledgement</h1>
          <p className="text-[#A0AEC0] mt-2">Please review and accept before continuing</p>
        </div>

        {/* Acknowledgement Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Document Header */}
          <div className="bg-gradient-to-r from-[#0D1B33] to-[#1a2d4d] px-6 py-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-[#176DFF]" />
            <span className="text-white font-medium">Leynix Pilot Program Agreement</span>
            <span className="ml-auto text-xs text-[#A0AEC0] bg-white/10 px-2 py-1 rounded">v1.0</span>
          </div>

          {/* Scrollable Content */}
          <div className="p-6 max-h-[400px] overflow-y-auto bg-gray-50">
            <div className="prose prose-sm max-w-none">
              {ACKNOWLEDGEMENT_TEXT.split('\n\n').map((paragraph, index) => {
                if (paragraph.startsWith('Welcome')) {
                  return (
                    <h2 key={index} className="text-xl font-semibold text-[#0D1B33] mb-4">
                      {paragraph}
                    </h2>
                  );
                }
                if (paragraph.startsWith('•')) {
                  return (
                    <div key={index} className="flex items-start gap-2 mb-3">
                      <CheckCircle2 className="w-4 h-4 text-[#176DFF] mt-0.5 flex-shrink-0" />
                      <p className="text-[#334155] text-sm leading-relaxed m-0">
                        {paragraph.replace('• ', '')}
                      </p>
                    </div>
                  );
                }
                return (
                  <p key={index} className="text-[#334155] text-sm leading-relaxed mb-4">
                    {paragraph}
                  </p>
                );
              })}
            </div>
          </div>

          {/* Acceptance Section */}
          <div className="p-6 border-t border-gray-200 bg-white">
            {/* Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group mb-4">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    setChecked(e.target.checked);
                    setError(null);
                  }}
                  className="sr-only"
                  data-testid="acknowledgement-checkbox"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  checked 
                    ? 'bg-[#176DFF] border-[#176DFF]' 
                    : 'border-gray-300 group-hover:border-[#176DFF]'
                }`}>
                  {checked && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-[#334155] leading-relaxed">
                I have read and understand the Pilot Participation Acknowledgement and agree to participate in the Leynix Pilot Program.
              </span>
            </label>

            {/* User Identity Display */}
            {userName && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-[#64748B] mb-1">Accepting as:</p>
                <p className="text-sm font-medium text-[#0D1B33]">{userName}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Accept Button */}
            <Button
              onClick={handleAccept}
              disabled={submitting || !checked}
              className="w-full h-12 bg-[#176DFF] hover:bg-[#1258cc] text-white font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="accept-acknowledgement-btn"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Recording Acceptance...
                </span>
              ) : (
                'Accept & Continue'
              )}
            </Button>

            {/* Footer Note */}
            <p className="text-xs text-[#94A3B8] text-center mt-4">
              Your acceptance will be recorded with timestamp and stored in the audit log.
            </p>
          </div>
        </div>

        {/* Leynix Branding */}
        <div className="text-center mt-6">
          <p className="text-xs text-[#64748B]">
            © 2026 Leynix · Developed and owned by Anessa Leydig
          </p>
        </div>
      </div>
    </div>
  );
}
